import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { STALE_TIMES } from '@/api/queryClient';
import type { Contact } from '@/api';

/*
 * The address-book query and its background warm-up, apart from the matching
 * code in useRecipientDirectory / recipientSuggestions: the app shell runs the
 * warm-up on every page, and importing it from there put the whole recipient
 * ranking on the first load (#577 follow-up, batch 2026-10-07e).
 */

/** Contacts per request; the backend's own default is 200 and its ceiling 1000. */
const PAGE_SIZE = 500;

/** Ceiling on what is held for matching. */
export const MAX_CONTACTS = 2000;

async function fetchAllContacts(
  api: ReturnType<typeof useApi>,
  accountId: string,
  signal: AbortSignal,
): Promise<Contact[]> {
  const books = await api.contacts.listBooks(accountId, signal);
  const all: Contact[] = [];
  for (const book of books) {
    let cursor: string | undefined;
    do {
      const page = await api.contacts.list(
        accountId,
        { addressBookId: book.id, limit: PAGE_SIZE, ...(cursor === undefined ? {} : { cursor }) },
        signal,
      );
      all.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor !== undefined && all.length < MAX_CONTACTS);
    if (all.length >= MAX_CONTACTS) break;
  }
  return all.slice(0, MAX_CONTACTS);
}

/**
 * The one description of the query, so the lazy read below and the background
 * warm-up share a cache entry rather than fetching the address book twice.
 */
export function directoryQuery(api: ReturnType<typeof useApi>, accountId: string) {
  return {
    queryKey: queryKeys.account(accountId).recipientDirectory(),
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchAllContacts(api, accountId, signal),
    staleTime: STALE_TIMES.metadata,
    retry: false,
  };
}

/** Runs `fn` when the browser is idle; `setTimeout` where that does not exist. */
function whenIdle(fn: () => void): () => void {
  const idle = (window as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof idle === 'function') {
    const handle = idle(fn, { timeout: 5_000 });
    return () => (window as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(fn, 1_500);
  return () => window.clearTimeout(handle);
}

/**
 * Warms the address book in the background once someone is signed in.
 *
 * Fetched only when a composer opened, the first compose of a session sat
 * silent for as long as CardDAV took to answer -- ~3 s against the live
 * server, which reads as broken even though an address typed in full always
 * worked. Nothing needs it before then, so this waits for the browser to be
 * idle rather than competing with the mail list's own first paint, and it goes
 * through the same query as the composer: whichever gets there first, the
 * other finds the answer already in the cache.
 *
 * Skipped while offline or when the device asked for less data -- a whole
 * address book is exactly the kind of speculative fetch Data Saver means.
 */
export function usePrefetchRecipientDirectory(accountId: string | undefined): void {
  const api = useApi();
  const client: QueryClient = useQueryClient();
  useEffect(() => {
    if (accountId === undefined) return undefined;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return undefined;
    const connection = (navigator as { connection?: { saveData?: boolean } }).connection;
    if (connection?.saveData === true) return undefined;
    return whenIdle(() => {
      void client.prefetchQuery(directoryQuery(api, accountId));
    });
  }, [accountId, api, client]);
}
